import { PrintSheetHostContext } from '@/dashboard/ui/PrintSheet';
import { type BillingDocumentPreview } from '@/dashboard/billing/PatientBillingWorkspace';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchPatientMasterSearch, fetchPatientMasterRecord, fetchPatient360Summary,
  fetchPatientTimeline, fetchPatientReceipts, fetchPatientBills,
  fetchCheckoutHandoff, getIntakePrefill, fetchPatientBookingDetails,
} from '@/dashboard/api';
import type { TodayAppointment } from '@/dashboard/types';
import { AcButton, LoadingState, FieldGrid } from '@/dashboard/ui';
import { PatientReceiptsTable } from './PatientRecordPage';
import ReceiptPrintView from '@/dashboard/billing/ReceiptPrintView';
import BillDetailCard from '@/dashboard/billing/BillDetailCard';
import BillPrintView from '@/dashboard/billing/BillPrintView';
import { billListMatchesScope, readPatientBillListWire } from '@/dashboard/billing/bill-view';
import { receiptListMatchesScope, formatMinorUnitsInr } from '@/dashboard/billing/billing-entry-view';
import type { Patient360AppointmentWire } from './patient-360-view';
import { genderAgeLabel } from './patient-master-view';
import { formatIstDateTime } from '@/lib/datetime-ist';
import { clinicLabelOf, useClinicLens } from '@/dashboard/clinic-context';
import { bookingChannelLabel, dayStatusChip } from '@/dashboard/appointments/ClinicDayBoard';

export const QUICK_DETAIL_LABELS = {
  registration: 'Registration details', states: 'All states', visits: 'All visits',
  payments: 'All payments', bill: 'Itemised bill', estimate: 'Saved treatment estimate',
} as const;
export type QuickDetail = keyof typeof QUICK_DETAIL_LABELS;
export function readQuickDetail(value: string | null): QuickDetail | null {
  return value !== null && Object.prototype.hasOwnProperty.call(QUICK_DETAIL_LABELS, value)
    ? value as QuickDetail : null;
}

// A keyed result and cleanup fence prevent a late response from appearing under
// another patient, appointment or child panel. Reads never create clinical/money data.
function useRead<T>(key: string, read: () => Promise<T>) {
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState<{key: string; value: T} | null>(null);
  const [error, setError] = useState<{key: string; message: string} | null>(null);
  useEffect(() => {
    let current = true;
    setResult(null); setError(null);
    read().then(value => { if (current) setResult({key, value}); }).catch(() => {
      if (current) setError({key, message: 'Details unavailable. Try again.'});
    });
    return () => { current = false; };
    // Callers include all read inputs in key, not the inline function identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, retry]);
  return { value: result?.key === key ? result.value : null,
    error: error?.key === key ? error.message : null, retry: () => setRetry(n => n + 1) };
}
function ReadState({ error, retry }: {error: string | null; retry: () => void}) {
  return error ? <div role="alert"><p>{error}</p><AcButton size="sm" onClick={retry}>Try again</AcButton></div>
    : <LoadingState label="Loading saved details..." lines={3} />;
}

type QuickDetailsProps = { patientName?: string; clinicId: number; onBack: () => void;
  onDocumentPreviewChange?: (preview: BillingDocumentPreview) => void;
} & ({ row: TodayAppointment; patientId?: never; detail: QuickDetail }
  | { row?: never; patientId: string; detail: 'visits' | 'payments' });
export default function PatientQuickDetails({ row, patientId, patientName, clinicId, detail, onBack }: QuickDetailsProps) {
  const identity = useRead(`${clinicId}:${row?.id}:${row?.patientId ?? patientId}:${row?.linkedPatientUmr}`, async () => {
    if (!row) {
      if (!patientId || !['visits', 'payments'].includes(detail)) throw new Error('Patient history unavailable');
      const summary = await fetchPatient360Summary(patientId);
      if (summary.patientId !== patientId) throw new Error('Patient unavailable');
      return {id: patientId, summary};
    }
    if (row.hasPatientRecord !== true) throw new Error('No verified patient record');
    let id = row.patientId;
    const umr = row.linkedPatientUmr?.trim();
    if (umr) {
      const result = await fetchPatientMasterSearch(umr, 'umr');
      const matches = result.candidates.filter(candidate => candidate.umr.toUpperCase() === umr.toUpperCase());
      if (matches.length !== 1) throw new Error('Ambiguous record');
      id = matches[0]!.patientId;
    }
    if (!id) throw new Error('Missing patient');
    const summary = await fetchPatient360Summary(id, row.id);
    if (summary.patientId !== id || summary.selectedAppointment?.appointmentId !== row.id ||
        summary.selectedAppointment.clinicId !== clinicId) throw new Error('Visit unavailable');
    return {id, summary};
  });
  const [params, setParams] = useSearchParams();
  const patientScope = `${clinicId}:${row?.id}:${row?.patientId ?? patientId}`;
  const priorPatientScope = useRef(patientScope);
  useEffect(() => {
    if(priorPatientScope.current===patientScope)return;
    priorPatientScope.current=patientScope;
    const next=new URLSearchParams(params);next.delete('billingDestination');next.delete('quickDocument');setParams(next,{replace:true});
  },[patientScope,params,setParams]);
  const lens = useClinicLens();
  const visitId = Number(params.get('quickVisit'));
  const documentId = Number(params.get('quickDocument'));
  const hasVisit = detail === 'visits' && Number.isSafeInteger(visitId) && visitId > 0;
  const hasDocument = (detail === 'bill' || detail === 'payments') && Number.isSafeInteger(documentId) && documentId > 0;
  const embeddedReceipt = detail === 'payments' && hasDocument;
  const focus = useRef<HTMLHeadingElement>(null);
  const root = useRef<HTMLElement>(null);
  const returning = useRef<string | null>(null);
  const levelKey = `${detail}:${hasVisit ? visitId : ''}:${hasDocument ? documentId : ''}`;
  useEffect(() => {
    let restored = false;
    const restore = () => {
      const target = returning.current ? root.current?.querySelector<HTMLElement>(returning.current.startsWith('visit-')
        ? `[data-quick-entry="${returning.current}"]` : `[data-testid="patient-documents-print-${row && detail === 'bill' ? 'bill' : 'receipt'}-${returning.current.slice(9)}"]`) : null;
      if (target) { target.focus(); returning.current = null; restored = true; }
    };
    const frame = requestAnimationFrame(() => { if (!restored) (focus.current ?? root.current)?.focus(); restore(); });
    const observer = new MutationObserver(restore);
    if (root.current) observer.observe(root.current, {childList: true, subtree: true});
    return () => { cancelAnimationFrame(frame); observer.disconnect(); };
  }, [levelKey, identity.value, detail]);
  const back = () => {
    if (hasVisit || hasDocument) {
      returning.current = hasVisit ? `visit-${visitId}` : `document-${documentId}`;
      const next = new URLSearchParams(params);
      next.delete('quickVisit'); next.delete('quickDocument'); setParams(next);
    } else onBack();
  };
  const openChild = (kind: 'quickVisit' | 'quickDocument', id: number) => {
    const next = new URLSearchParams(params); next.set(kind, String(id)); setParams(next);
  };
  return <section ref={root} tabIndex={-1} data-testid="home-quick-details" onKeyDown={event => {
    if (event.key === 'Escape') { event.stopPropagation(); back(); }
  }}>
    {detail !== 'bill' ? <AcButton size="sm" variant="ghost" onClick={back} data-testid="home-quick-details-back">Back</AcButton> : null}
    {detail !== 'bill' && !embeddedReceipt ? <h3 ref={focus} tabIndex={-1}>{patientName ?? (hasVisit ? 'Saved appointment / visit' : hasDocument ? 'Document preview' : QUICK_DETAIL_LABELS[detail])}</h3> : null}
    {identity.value === null ? <ReadState error={identity.error} retry={identity.retry} /> : <>
      {detail !== 'bill' && !embeddedReceipt && !hasVisit ? <p>UMR {identity.value.summary.umr}{row ? ` · Appointment #${row.id}` : ''}</p> : null}
      {row && detail === 'registration' ? <RegistrationDetail patientId={identity.value.id} appointmentId={row.id} /> : null}
      {row && detail === 'states' ? <VisitDetail patientId={identity.value.id} appointmentId={row.id} /> : null}
      {detail === 'visits' ? hasVisit
        ? <VisitDetail compact key={visitId} patientId={identity.value.id} appointmentId={visitId} />
        : <VisitList patientId={identity.value.id} knownVisits={[...(identity.value.summary.todayAppointments ?? []), ...(identity.value.summary.recentVisits ?? []), ...(identity.value.summary.upcomingAppointments ?? [])]} onSelect={id => openChild('quickVisit', id)} /> : null}
      {row && detail === 'bill' ? <ReceptionBillRead key={`${identity.value.id}:${clinicId}:${row.id}`}
        patientId={identity.value.id} appointmentId={row.id} clinicId={clinicId}
        clinicLabel={clinicLabelOf(lens?.options.find(option => option.clinicId === clinicId)?.clinicLabel)}
        selectedBillId={hasDocument ? documentId : null}
        onSelect={id => id === null ? back() : openChild('quickDocument', id)} onBack={onBack} /> : null}
      {detail === 'payments' ? <PrintSheetHostContext.Provider value={embeddedReceipt}><ReceiptDocuments clinicId={clinicId} key={hasDocument ? documentId : 'list'} patientId={identity.value.id}
        documentId={hasDocument ? documentId : null} onSelect={id => openChild('quickDocument', id)} onBack={back} /></PrintSheetHostContext.Provider> : null}
      {row && detail === 'estimate' ? <EstimateDetail patientId={identity.value.id} appointmentId={row.id} clinicId={clinicId} /> : null}
    </>}
  </section>;
}

function RegistrationDetail({patientId, appointmentId}: {patientId: string; appointmentId: number}) {
  const data = useRead(patientId, () => fetchPatientMasterRecord(patientId));
  if (!data.value) return <ReadState error={data.error} retry={data.retry} />;
  const record = data.value;
  if (record.patient.patientId !== patientId) return <p role="alert">Patient record unavailable.</p>;
  return <>
    <FieldGrid columns={1} testId="quick-registration-values" fields={[
      {label: 'Name', value: record.patient.fullName}, {label: 'UMR', value: record.patient.umr},
      {label: 'Age / gender', value: genderAgeLabel(record.patient.gender, record.patient.ageYears, record.patient.ageIsApproximate)},
      {label: 'Date of birth', value: record.patient.dob ?? 'Not recorded'},
      {label: 'City', value: record.patient.city ?? 'Not recorded'},
      {label: 'Registered at', value: record.patient.registeredClinicLabel ?? 'Clinic unavailable'},
      {label: 'Registered on', value: formatIstDateTime(record.patient.createdAt)},
    ]} />
    <h4>Contact details</h4>
    {record.contactPoints.map(point => <p key={point.id}>{point.type} · {point.hasValue ? 'Recorded · masked' : 'Not recorded'}{point.isPrimary ? ' · Primary' : ''}</p>)}
    {record.contactPoints.length === 0 ? <p>No contact details recorded.</p> : null}
    <h4>Guardian / related person</h4>
    {record.relatedPersons.map((person, i) => <p key={i}>{person.name ?? 'Name not recorded'} · {person.relationship}{person.isPrimaryGuardian ? ' · Primary guardian' : ''}</p>)}
    {record.relatedPersons.length === 0 ? <p>Not recorded.</p> : null}
    <h4>Consent</h4>
    {record.consents.map((consent, i) => <p key={i}>{consent.consentType} · {consent.grantedBy} · {consent.channel} · {formatIstDateTime(consent.grantedAt)}</p>)}
    {record.consents.length === 0 ? <p>Not recorded.</p> : null}
    <VisitRegistrationDetails patientId={patientId} appointmentId={appointmentId} />
    <p>Saved administrative details. Use the registration/check-in action to confirm required visit information.</p>
  </>;
}
function VisitRegistrationDetails({patientId, appointmentId}: {patientId: string; appointmentId: number}) {
  const data = useRead(`${patientId}:${appointmentId}`, () => getIntakePrefill(appointmentId));
  if (!data.value) return <><h4>Visit registration</h4><ReadState error={data.error} retry={data.retry} /></>;
  const saved = data.value;
  if (saved.appointmentId !== appointmentId || (saved.patientId !== null && saved.patientId !== patientId)) return <p role="alert">Visit registration unavailable.</p>;
  return <><h4>Visit registration</h4><FieldGrid columns={1} testId="quick-visit-registration" fields={[
    {label: 'Name title', value: saved.registrationNameTitle},
    {label: 'Last name', value: saved.registrationLastName},
    {label: 'Relation title', value: saved.registrationRelationTitle},
    {label: 'Relation name', value: saved.registrationRelationName},
    {label: 'Occupation', value: saved.registrationOccupation},
    {label: 'Handling category', value: saved.registrationHandlingCategory},
    {label: 'Door / building', value: saved.registrationAddressDoor},
    {label: 'Street', value: saved.registrationAddressStreet},
    {label: 'Area', value: saved.registrationAddressArea},
    {label: 'Landmark', value: saved.registrationAddressLandmark},
    {label: 'Taluka', value: saved.registrationAddressTaluka},
    {label: 'State', value: saved.registrationState},
    {label: 'Pincode', value: saved.registrationPincode},
    {label: 'Country', value: saved.registrationAddressCountry},
    {label: 'Email', value: saved.emailOnFile === true ? 'Recorded · masked' : saved.emailOnFile === false ? 'Not recorded' : 'Unavailable'},
  ].map(field => ({...field, missingLabel: field.value === undefined ? 'Unavailable' : 'Not recorded'}))} />{saved.pendingCorrections?.length ? <p>Proposed changes await confirmation in the registration form.</p> : null}</>;
}
function VisitList({patientId, knownVisits, onSelect}: {patientId: string; knownVisits: readonly Patient360AppointmentWire[]; onSelect: (id: number) => void}) {
  const data = useRead(patientId, () => fetchPatientTimeline(patientId));
  if (!data.value) return <ReadState error={data.error} retry={data.retry} />;
  if (data.value.patientId !== patientId) return <p role="alert">Visits unavailable.</p>;
  const visits = [...new Map(data.value.appointmentHistory.filter(row => row.appointmentId != null)
    .map(row => [row.appointmentId!, row])).values()].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
  return <><h4>Recorded appointments and visits</h4><ul className="patient-record-list">{visits.map(visit => <li key={visit.appointmentId}>
    <AcButton variant="ghost" size="sm" data-quick-entry={`visit-${visit.appointmentId}`} onClick={() => onSelect(visit.appointmentId!)}>
      {formatIstDateTime(visit.occurredAt)} · #{visit.appointmentId} · {visit.storedLabel}
    </AcButton>
    <p>{knownVisits.find(saved => saved.appointmentId === visit.appointmentId)?.doctorName ?? 'Doctor unavailable'}</p>
  </li>)}</ul>{visits.length === 0 ? <p>No saved visits returned.</p> : null}
    <h4>Recorded changes</h4>
    {data.value.eventStreamStatus === 'available' ? (data.value.entries.length ? <ul className="patient-record-list">{data.value.entries.map((entry,index) => <li key={index}>{entry.label} · {formatIstDateTime(entry.occurredAt)}</li>)}</ul> : <p>No changes recorded.</p>) : <p>Recorded changes unavailable.</p>}
  </>;
}
// Reuse the scoped booking read; never combine another patient's or clinic's descriptors.
function SavedBookingDetails({patientId, appointmentId, clinicId}: {patientId: string; appointmentId: number; clinicId: number}) {
  const data = useRead(`${patientId}:${clinicId}:${appointmentId}`, async () => {
    const saved = await fetchPatientBookingDetails(appointmentId);
    if (saved.patientId !== patientId || saved.appointmentId !== appointmentId || saved.clinicId !== clinicId) throw new Error('Booking unavailable');
    return saved;
  });
  if (!data.value) return <ReadState error={data.error} retry={data.retry} />;
  return <FieldGrid columns={1} testId="quick-booking-values" fields={[
    {label: 'Department / treatment', value: `${data.value.department ?? 'Not recorded'} · ${data.value.serviceName ?? 'Not recorded'}`},
    {label: 'Appointment type', value: data.value.visitType ? bookingChannelLabel(data.value.visitType) : null},
    {label: 'Booking channel', value: data.value.bookingChannel ? bookingChannelLabel(data.value.bookingChannel) : null},
    {label: 'Booking source', value: data.value.bookingSource?.replace(/_/g, ' '), missingLabel: data.value.bookingSource === undefined ? 'Unavailable' : 'Not recorded'},
  ]} />;
}

function VisitDetail({patientId, appointmentId, compact = false}: {patientId: string; appointmentId: number; compact?: boolean}) {
  const data = useRead(`${patientId}:${appointmentId}`, () => fetchPatient360Summary(patientId, appointmentId));
  if (!data.value) return <ReadState error={data.error} retry={data.retry} />;
  const visit = data.value.patientId === patientId ? data.value.selectedAppointment : null;
  if (!visit || visit.appointmentId !== appointmentId) return <p role="alert">This visit is unavailable for this patient.</p>;
  const history = data.value.selectedAppointmentHistory;
  const bookingEvent = history?.availability === 'available' && !history.capped
    ? [...history.items].filter(event => event.eventType === 'appointment_booked').sort((a,b) => a.occurredAt.localeCompare(b.occurredAt))[0] : undefined;
  const bookedBy = history?.availability !== 'available' || history.capped ? 'Unavailable'
    : bookingEvent ? `${bookingEvent.actorName ?? (bookingEvent.actorRole ? bookingEvent.actorRole.replace(/_/g, ' ') : 'Name not recorded')} · ${formatIstDateTime(bookingEvent.occurredAt)}` : 'Not recorded';
  return <><h4>Saved appointment / visit</h4><FieldGrid columns={1} testId="quick-visit-values" fields={[
    {label: 'Appointment', value: `#${visit.appointmentId}`},
    {label: 'Date / time', value: formatIstDateTime(visit.scheduledAt)},
    ...(compact ? [{label: 'Clinic / doctor', value: `${visit.clinicLabel ?? 'Clinic unavailable'} · ${visit.doctorName ?? 'Not recorded'}`}] : [
    {label: 'Clinic', value: visit.clinicLabel ?? 'Clinic unavailable'},
    {label: 'Doctor', value: visit.doctorName ?? 'Not recorded'}]),
    ...(!compact ? [
    {label: 'Visit state', value: dayStatusChip(visit.status).label},
    {label: 'Arrived', value: visit.arrivedAt ? formatIstDateTime(visit.arrivedAt) : 'Not recorded'},
    {label: 'Intake', value: visit.intake.availability === 'available' ? visit.intake.status.replace(/_/g, ' ') : 'Unavailable'},
    {label: 'Consultation', value: visit.clinicalState === 'unavailable' ? 'Unavailable' : visit.clinicalState.replace(/_/g, ' ')},
    {label: 'Consultation actor', value: visit.consultActor?.label ?? 'Not recorded'},
    {label: 'Payment evidence', value: visit.paymentRecorded ? 'Recorded' : 'Not recorded'},
    ] : []),
  ]} />
    <SavedBookingDetails patientId={patientId} appointmentId={appointmentId} clinicId={visit.clinicId} />
    {compact ? <><FieldGrid columns={1} fields={[{label:'Booked by',value:bookedBy},{label:'State',value:dayStatusChip(visit.status).label},...(visit.arrivedAt ? [{label:'Arrived',value:formatIstDateTime(visit.arrivedAt)}] : [])]} /><p>Read-only saved administrative record. Clinical notes are not included.</p></> : <>
    <h4>Recorded changes</h4>
    {history?.availability === 'available' ? <>
      {history.items.length ? <ul>{history.items.map((event, index) => <li key={index}>
        {event.eventType.replace(/_/g, ' ')} · {formatIstDateTime(event.occurredAt)} · {event.actorRole?.replace(/_/g, ' ') ?? 'Role not recorded'}
      </li>)}</ul> : <p>No changes recorded.</p>}
      {history.capped ? <p>Showing the first 200 recorded changes.</p> : null}
    </> : <p>Recorded changes unavailable.</p>}
    </>}
  </>;

}
function usePages<T>(key: string, load: (offset: number) => Promise<{items: readonly T[]; hasMore: boolean}>) {
  const [page, setPage] = useState({key, offset: 0, attempt: 0});
  const [result, setResult] = useState<{key: string; items: readonly T[]; hasMore: boolean} | null>(null);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const offset = page.key === key ? page.offset : 0;
  useEffect(() => {
    let current = true; setPending(true); setError(null);
    load(offset).then(next => {
      if (!current) return;
      setResult(previous => ({key, items: offset > 0 && previous?.key === key
        ? [...previous.items.slice(0, offset), ...next.items] : next.items, hasMore: next.hasMore}));
      setPending(false);
    }).catch(() => { if (current) { setError('Documents unavailable. Try again.'); setPending(false); } });
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, offset, page.attempt]);
  const visible = result?.key === key ? result : null;
  return {items: visible?.items ?? null, hasMore: visible?.hasMore === true, pending, error,
    retry: () => setPage({key, offset, attempt: page.attempt + 1}),
    older: () => { if (!pending && visible?.hasMore) setPage({key, offset: visible.items.length, attempt: page.attempt + 1}); }};
}
function ReceiptDocuments({patientId, clinicId, documentId, onSelect, onBack}: {
  clinicId: number; patientId: string; documentId: number | null; onSelect: (id: number) => void; onBack: () => void;
}) {
  return documentId === null ? <ReceiptList clinicId={clinicId} patientId={patientId} onSelect={onSelect} />
    : <ReceiptPreview clinicId={clinicId} patientId={patientId} documentId={documentId} onBack={onBack} />;
}
function ReceiptList({patientId, clinicId, onSelect}: {clinicId:number; patientId: string; onSelect: (id: number) => void}) {
  const data = usePages(`${clinicId}:${patientId}`, async offset => {
    const result = await fetchPatientReceipts({patientId, clinicId, offset, limit: 20});
    if (!receiptListMatchesScope(result,{patientId,clinicId,paymentId:null})) throw new Error('Receipt mismatch');
    return {items: result.payments, hasMore: result.hasMore === true};
  });
  return <>{data.items === null ? <ReadState error={data.error} retry={data.retry} />
    : data.items.length ? <PatientReceiptsTable receipts={data.items} onPrintReceipt={payment => onSelect(payment.id)} /> : <p>No receipts recorded.</p>}
    {data.items !== null && data.error ? <ReadState error={data.error} retry={data.retry} /> : null}
    {data.hasMore ? <AcButton disabled={data.pending} size="sm" onClick={data.older}>{data.pending ? 'Loading...' : 'Show older receipts'}</AcButton> : null}</>;
}
function ReceiptPreview({patientId, clinicId, documentId, onBack}: {clinicId:number; patientId: string; documentId: number; onBack: () => void}) {
  const lens = useClinicLens();
  const data = useRead(`${clinicId}:${patientId}:${documentId}`, async () => {
    const result = await fetchPatientReceipts({patientId, clinicId, paymentId: documentId, limit: 1});
    if(!receiptListMatchesScope(result,{patientId,clinicId,paymentId:documentId}))throw new Error('Receipt scope unavailable');
    const payment = result.payments.find(row => row.id === documentId);
    if (!payment) throw new Error('Receipt unavailable');
    return payment;
  });
  return data.value ? <ReceiptPrintView payment={data.value} clinicLabel={clinicLabelOf(lens?.options.find(option => option.clinicId === data.value!.clinicId)?.clinicLabel)} backLabel="Back to payments" onBack={onBack} />
    : <ReadState error={data.error} retry={data.retry} />;
}
function EstimateDetail({patientId, appointmentId, clinicId}: {patientId: string; appointmentId: number; clinicId: number}) {
  const data = useRead(`${patientId}:${appointmentId}:${clinicId}`, () => fetchCheckoutHandoff(appointmentId));
  if (!data.value) return <ReadState error={data.error} retry={data.retry} />;
  const saved = data.value;
  if (saved.appointmentId !== appointmentId || saved.clinicId !== clinicId || (saved.patientId !== null && saved.patientId !== patientId)) return <p role="alert">Estimate unavailable for this visit.</p>;
  return <><p>Saved treatment estimate · separate from a bill or payment.</p>
    {saved.plan === null ? <p>No saved treatment plan returned.</p> : saved.plan.planItems ?
      <ul className="patient-record-list">{saved.plan.planItems.map(item => <li key={item.id}>
        <strong>{item.procedureName}</strong><p>{item.status} · {item.estimatedValue === null ? 'Price unavailable' : formatMinorUnitsInr(item.estimatedValue * 100)}</p>
      </li>)}</ul> : <p>Item details are unavailable to this role.</p>}
    <FieldGrid columns={1} testId="quick-estimate-values" fields={[
      {label: 'Recommended estimate', value: saved.estimate.recommendedValueInr === null ? 'Unavailable' : formatMinorUnitsInr(saved.estimate.recommendedValueInr * 100)},
      {label: 'Accepted estimate', value: saved.estimate.acceptedValueInr === null ? 'Unavailable' : formatMinorUnitsInr(saved.estimate.acceptedValueInr * 100)},
      {label: 'Prices not recorded', value: String(saved.estimate.unknownPriceItemCount)},
    ]} />
  </>;
}

/** Reception composes Money's scoped reader and immutable document renderers.
 * It never mounts Money's bill editor or receipt-correction controls. */
function ReceptionBillRead({patientId, appointmentId, clinicId, clinicLabel, selectedBillId, onSelect, onBack}: {
  patientId: string; appointmentId: number; clinicId: number; clinicLabel: string;
  selectedBillId: number | null; onSelect: (id: number | null) => void; onBack: () => void;
}) {
  const [offset, setOffset] = useState(0);
  const [printing, setPrinting] = useState(false);
  const key = JSON.stringify([patientId, clinicId, appointmentId, selectedBillId, offset]);
  const data = useRead(key, async () => {
    const raw = await fetchPatientBills({patientId, clinicId, appointmentId,
      ...(selectedBillId === null ? {} : {billId: selectedBillId}), limit: 20, offset: selectedBillId === null ? offset : 0});
    const parsed = readPatientBillListWire(raw);
    if (parsed.kind !== 'ready' || !billListMatchesScope(raw,
      {patientId, clinicId, appointmentId, billId: selectedBillId})) throw new Error('Bill unavailable');
    return {bills: parsed.bills, hasMore: raw.hasMore === true};
  });
  const selected = data.value?.bills.find(bill => bill.id === selectedBillId);
  const printFocus = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // The opener unmounts when the immutable preview opens. Keep keyboard
    // focus inside the child so Escape reaches the existing return handler.
    if (printing && selected) printFocus.current?.focus();
  }, [printing, selected?.id]);
  if (printing && selected) return <PrintSheetHostContext.Provider value>
    <div ref={printFocus} tabIndex={-1} data-testid="reception-bill-print-focus">
      <BillPrintView bill={selected} clinicLabel={clinicLabel} backLabel="Back to bill" onBack={() => setPrinting(false)} />
    </div>
  </PrintSheetHostContext.Provider>;
  return <section aria-label="Itemised bill" data-testid="reception-readonly-bill">
    <AcButton variant="ghost" onClick={() => selectedBillId === null ? onBack() : onSelect(null)}>Back</AcButton>
    {data.value === null ? <ReadState error={data.error} retry={data.retry} /> : selected ? <>
      <BillDetailCard bill={selected} clinicLabel={clinicLabel} documentLayout />
      <AcButton onClick={() => setPrinting(true)}>Print / Save PDF</AcButton>
    </> : selectedBillId !== null ? <p>Bill unavailable for this visit.</p> : <>
      {data.value.bills.length === 0 ? <p>No bills recorded for this visit.</p> : data.value.bills.map(bill =>
        <div key={bill.id}><AcButton variant="ghost" data-testid={`patient-documents-print-bill-${bill.id}`} onClick={() => onSelect(bill.id)}>Bill #{bill.id}</AcButton>
          <span>{formatIstDateTime(bill.createdAt)}</span></div>)}
      {offset > 0 ? <AcButton variant="ghost" onClick={() => setOffset(value => Math.max(0, value - 20))}>Previous</AcButton> : null}
      {data.value.hasMore ? <AcButton variant="ghost" onClick={() => setOffset(value => value + 20)}>Next</AcButton> : null}
    </>}
  </section>;
}
